/**
 * Jauge de mémoire d'une conversation — contrat UNIQUE entre les deux agents v3
 * (Case Discovery, Édition) et leurs deux fronts (web, add-in Word).
 *
 * Voyage par trois canaux : `context_usage_update.payload`, `agent_done.payload.memoryUsage`
 * (WebSocket, pendant un tour) et `GET /api/case-discovery-v3/conversations/:id/memory-usage`
 * (REST, à la réouverture d'une conversation — M186). Il vivait en quatre copies
 * (backend lib/llm, backend wire Édition, web, add-in) qui divergeaient déjà sur
 * `contextWindow` (obligatoire côté émetteur, optionnel côté fronts).
 *
 * La jauge est rapportée au SEUIL de compaction automatique, jamais à la fenêtre du
 * modèle : 100 % = la compaction arrive. Construite côté backend par
 * `buildChatContextGauge` (lib/llm/context/compactionPolicy.ts).
 */
import { z } from 'zod';

export const MemoryUsageSchema = z.object({
  /** Tokens estimés du contexte envoyé au modèle (messages + outils). */
  tokensUsed: z.number().nonnegative(),
  /** Référence de la jauge = seuil de compaction automatique (égal à `compactionThreshold`). */
  tokensMax: z.number().nonnegative(),
  /** `min(100, tokensUsed / compactionThreshold × 100)`, une décimale. */
  percentUsed: z.number().min(0).max(100),
  /** Seuil de compaction automatique (400k). */
  compactionThreshold: z.number().nonnegative(),
  /** Fenêtre nominale du modèle (1M), purement informative — jamais le dénominateur. */
  contextWindow: z.number().nonnegative(),
  /** true au-delà de 80 % du seuil. */
  willCompactSoon: z.boolean(),
});

export type MemoryUsage = z.infer<typeof MemoryUsageSchema>;
